import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody, ModalFooter, Button, Row, Col, Label, FormGroup } from "reactstrap";
import i18n from "../../../i18n";
import Select from "react-select";
import SimpleAlertError from "../../../components/Alerts/SimpleAlertError";
import { delCostCenterEmployees, getCostCentersAdmin, putCostCenterEmployees, putCostCenterUpdatePolicyEmployee } from "../../../helpers/api_rombo_helper";

const DeleteEmployeeModal = ({ selectCostCenter, isOpen, toggle, setShowAlertSuccess, setDeleteEmployeeModal, setTextHeader, setTextBody, selectEmployee, setReloadCostCenterEmployees, reloadCostCenterEmployees, token, partnerId }) => {

  const [errorAlert, setErrorAlert] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState(null);
  const [costCenters, setCostCenters] = useState([]);
  const [selectedNewCostCenter, setSelectedNewCostCenter] = useState(null);
  const [deleteWithoutReassign, setDeleteWithoutReassign] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && partnerId) {
      loadCostCenters();
    }
  }, [isOpen, partnerId]);

  useEffect(() => {
    if (deleteWithoutReassign) {
      setSelectedNewCostCenter(null);
    }
  }, [deleteWithoutReassign]);

  const loadCostCenters = async () => {
    try {
      setLoading(true);
      const response = await getCostCentersAdmin(token, { 
        partner_id: partnerId,
        page: 1, 
        size: 100 
      });
      const filteredCostCenters = response.items.filter(
        cc => cc.id !== selectCostCenter.id
      );
      const options = filteredCostCenters.map(cc => ({
        value: cc.id,
        label: cc.name
      }));
      setCostCenters(options);
    } catch (error) {
      console.error("Error loading cost centers:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetAvailableTrips = async () => {
    try {
      await putCostCenterUpdatePolicyEmployee(token, selectCostCenter.id, selectEmployee.idDB, { total_trips: 0 });
    } catch (error) {
      console.error("Error resetting available trips:", error);
    }
  };

  const onClickAccept = async () => {
    if (!deleteWithoutReassign && !selectedNewCostCenter) {
      setErrorAlertMessage(i18n.t("SelectCostCenterOrCheckbox"));
      setErrorAlert(true);
      return;
    }

    try {
      const deleteBody = { 
        employees: [{ id: selectEmployee.idDB }]
      };

      await resetAvailableTrips();
      await delCostCenterEmployees(token, selectCostCenter.id, deleteBody);

      if (!deleteWithoutReassign && selectedNewCostCenter) {
        const addBody = {
          employees: [{ id: selectEmployee.idDB }]
        };
        await putCostCenterEmployees(token, selectedNewCostCenter.value, addBody);
      }

      setShowAlertSuccess(true);
      setDeleteEmployeeModal(false);
      setReloadCostCenterEmployees(!reloadCostCenterEmployees);
      
      if (!deleteWithoutReassign && selectedNewCostCenter) {
        setTextHeader(i18n.t("MovedEmployee"));
        setTextBody(
          i18n.t("SuccessMoveEmployee", {
            name: selectEmployee.name,
            fromCostCenter: selectCostCenter.name,
            toCostCenter: selectedNewCostCenter.label
          })
        );
      } else {
        setTextHeader(i18n.t("DeletedEmployee"));
        setTextBody(
          i18n.t("SuccessDeleteEmployee", {
            name: selectEmployee.name,
            costCenter: selectCostCenter.name
          })
        );
      }
      
      resetState();
    } catch (error) {
      console.error("Error - delCostCenterEmployees - ", error);
      const msg = error.response?.data?.detail || error.message;
      setErrorAlertMessage(error.code === 500 ? i18n.t("NetworkError") : msg);
      setErrorAlert(true);
    }
  };

  const resetState = () => {
    setSelectedNewCostCenter(null);
    setDeleteWithoutReassign(false);
    setErrorAlert(false);
    setErrorAlertMessage(null);
  };

  const handleCancel = () => {
    resetState();
    toggle();
  };

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#5A6EDF' : state.isFocused ? '#5A6EDF' : 'white',
      color: state.isSelected ? 'white' : state.isFocused ? 'white' : '#212121',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:active': {
        backgroundColor: '#4A5DC0',
      },
    }),
  };

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      autoFocus
      centered
      className="RequestModal"
      style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      toggle={handleCancel}
      backdrop="static"
      id="staticBackdrop"
      size="lg"
    >
      <div style={{ position: "relative" }}>
        <div style={{ 
          position: "absolute", 
          top: "16px", 
          right: "16px", 
          color: "#9e9e9e",
          fontSize: "0.875rem"
        }}>
          {selectEmployee.name}
        </div>
        <p className="cancellation-title">
          {i18n.t("DeleteEmployee")}
        </p>
        <div className="modal-separator" />
        <ModalBody className="mt-0 pt-0">
          <SimpleAlertError
            errorAlert={errorAlert}
            setErrorAlert={setErrorAlert}
            text={errorAlertMessage}
          />
          <Row className="mt-4">
            <Col>
              <p className="mb-3">{i18n.t("DeleteEmployeeRequireReassign")}</p>
              <p className="mb-4">{i18n.t("SelectNewCostCenter")}</p>
              <Select
                options={costCenters}
                value={selectedNewCostCenter}
                onChange={setSelectedNewCostCenter}
                placeholder={i18n.t("SelectCostCenter")}
                isClearable
                isDisabled={deleteWithoutReassign || loading}
                isLoading={loading}
                styles={customStyles}
                className="mb-4"
              />
              <FormGroup className="mb-3">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                    userSelect: "none"
                  }}
                  onClick={() => setDeleteWithoutReassign(prev => !prev)}
                >
                  <input
                    id="deleteWithoutReassignSwitch"
                    type="checkbox"
                    checked={deleteWithoutReassign}
                    onChange={(e) => setDeleteWithoutReassign(e.target.checked)}
                    style={{ display: "none" }}
                  />
                  <span
                    style={{
                      position: "relative",
                      width: "42px",
                      height: "24px",
                      backgroundColor: deleteWithoutReassign ? "#5A6EDF" : "#D1D5DB",
                      borderRadius: "999px",
                      transition: "background-color 0.25s ease"
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: "3px",
                        left: deleteWithoutReassign ? "21px" : "3px",
                        width: "18px",
                        height: "18px",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        transition: "left 0.25s ease"
                      }}
                    />
                  </span>
                  <Label
                    htmlFor="deleteWithoutReassignSwitch"
                    className="mb-0"
                    style={{ cursor: "pointer" }}
                  >
                    {i18n.t("DeleteWithoutReassign")}
                  </Label>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn-users border"
            type="button"
            onClick={onClickAccept}
          >
            {i18n.t("Accept")}
          </Button>
          <Button
            className="btn-light"
            type="button"
            onClick={handleCancel}
          >
            {i18n.t("GoBack")}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

DeleteEmployeeModal.propTypes = {
  selectCostCenter: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  setShowAlertSuccess: PropTypes.func.isRequired,
  setDeleteEmployeeModal: PropTypes.func.isRequired,
  setTextHeader: PropTypes.func.isRequired,
  setTextBody: PropTypes.func.isRequired,
  selectEmployee: PropTypes.object.isRequired,
  setReloadCostCenterEmployees: PropTypes.func.isRequired,
  reloadCostCenterEmployees: PropTypes.bool.isRequired,
  token: PropTypes.object.isRequired,
  partnerId: PropTypes.number.isRequired
};

export default DeleteEmployeeModal;
